import {
  IssueRelation,
  IssueWinChances,
  Quantum,
  WinChances,
} from "../../shared/CaseModel";
import { enteredChance, WinChanceInput } from "../../shared/winChance";

/**
 * What the calculation makes of the rows of a claim — the claim, its issues
 * and groups (see {@link IssueWinChances}) — and whether it is working out a
 * change that is not in them yet. The claims page shows these, never figures
 * of its own.
 */
export type RowChances = {
  of: (rowId: string) => IssueWinChances | undefined;
  calculating: boolean;
};

/** No figures: for a claim shown before anything is calculated. */
export const NO_ROW_CHANCES: RowChances = {
  of: () => undefined,
  calculating: false,
};

export interface Party {
  id: string;
  name: string;
}

export interface Issue {
  id: string;
  title: string;
  relation: IssueRelation;
  // if set, issue is a generic one and defendant win chances shall be discarded
  generic?: boolean;
  genericWinChance?: number;
  // who the issue runs against and their chances (see WinChances); only in
  // effect when the issue is not generic
  winChanceAgainst: WinChances;
}

export interface IssueGroup {
  id: string;
  subIssues: Issue[];
  relation: IssueRelation;
}

export type Defendant = Party & { settled?: boolean };

export interface Claim {
  id: string;
  title: string;
  claimant: Party;
  defendants: Defendant[];
  issues: (Issue | IssueGroup)[];
  quantums?: Quantum[];
  // for claims without issues
  generic?: boolean;
  genericWinChance?: number;
  // only in effect when genericWinChance not set
  winChanceAgainst?: WinChances;
  type?: string;
}

export function isGroup(element: unknown | IssueGroup): element is IssueGroup {
  return (element as IssueGroup).subIssues !== undefined;
}

export function isIssue(element: Issue | IssueGroup): element is Issue {
  return (element as Issue).winChanceAgainst !== undefined;
}

/** Who the issue runs against: the defendants its win chances name. */
export function involvedDefendantIds(issue: Issue): string[] {
  return Object.keys(issue.winChanceAgainst);
}

/**
 * The issue in the shape the shared win-chance predicates read, so the table's
 * markers and the analytics gate cannot disagree about what counts as entered.
 * An issue runs against the defendants its win chances name; a claim without
 * issues passes all its defendants as `involved`.
 */
export function winChanceInputOf(
  issue: {
    generic?: boolean;
    genericWinChance?: number;
    winChanceAgainst?: WinChances;
  },
  involved: string[] = Object.keys(issue.winChanceAgainst ?? {})
): WinChanceInput {
  return {
    generic: issue.generic,
    genericWinChance: issue.genericWinChance,
    perDefendantWinChance: issue.winChanceAgainst,
    involved,
  };
}

/**
 * Makes the defendant part of every leaf issue WITHOUT inventing a chance: it
 * joins as `null` (see WinChances). An unset chance is the user's decision
 * still to make: it shows as missing and blocks the analytics, whereas a
 * filled-in 0% would silently read as a certain loss the user never chose.
 */
export function involveDefendant(
  defendantId: string,
  issues: (Issue | IssueGroup)[]
): void {
  issues.forEach((issue) => {
    if (isGroup(issue)) involveDefendant(defendantId, issue.subIssues);
    else if (!(defendantId in issue.winChanceAgainst))
      issue.winChanceAgainst[defendantId] = null;
  });
}

/**
 * Replaces the set of defendants a leaf issue runs against. Newly selected
 * defendants join without a chance (see {@link involveDefendant}); chances
 * entered for deselected ones are dropped with them.
 */
export function setInvolvedDefendants(
  issue: Issue,
  defendantIds: string[]
): void {
  for (const defendantId of Object.keys(issue.winChanceAgainst)) {
    if (!defendantIds.includes(defendantId))
      delete issue.winChanceAgainst[defendantId];
  }
  for (const defendantId of defendantIds) {
    if (!(defendantId in issue.winChanceAgainst))
      issue.winChanceAgainst[defendantId] = null;
  }
}

/**
 * Switches a generic issue (or claim) to per-defendant chances. As generic it
 * ran against every one of `defendants` at the generic chance, so each starts
 * from that chance — what the calculation already assumed, and nothing left
 * unrated. Without a generic chance they start without one (`null`). Chances
 * entered before the issue went generic had no effect since and are replaced.
 */
export function switchToSpecific(
  issue: { generic?: boolean; genericWinChance?: number; winChanceAgainst?: WinChances },
  defendants: Party[]
): void {
  issue.generic = false;
  issue.winChanceAgainst = Object.fromEntries(
    defendants.map(({ id }) => [id, issue.genericWinChance ?? null])
  );
}

/** The chance entered for a defendant on an issue: its one chance if generic. */
function getWinChance(issue: Issue, defendantId: string): number | undefined {
  return issue.generic
    ? (issue.genericWinChance ?? 0)
    : enteredChance(issue.winChanceAgainst, defendantId);
}

/**
 * Per sub-issue cap on a defendant's win chance in an XOR group, where the
 * chances must sum to at most 1: each sub-issue may take its current chance
 * plus the share no sub-issue claims yet. A defendant involved in a sub-issue
 * but not yet rated there claims no share, so it is capped at that unclaimed
 * share too rather than left unlimited. Never below 0, so a group already over
 * 1 cannot drive a slider negative.
 */
export function getXorWinChanceLimits(
  issueGroup: IssueGroup,
  defendant: Party
): { [issueId: string]: number } {
  let totalWinChance = 0;
  const winChances: { [issueId: string]: number } = {};
  issueGroup.subIssues.forEach((subIssue) => {
    const winChance = getWinChance(subIssue, defendant.id) ?? 0;
    totalWinChance += winChance;
    winChances[subIssue.id] = winChance;
  });

  const remaining = 1 - totalWinChance;
  for (const issueId in winChances) {
    winChances[issueId] = Math.max(0, winChances[issueId] + remaining);
  }
  return winChances;
}

